import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Send, Clock, CheckCircle, XCircle, FileText, RefreshCw, ExternalLink, Upload, CheckSquare, Phone, Search, Shield, Calendar, X } from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { AppLayout } from "@/components/app-layout";
import { useState, useEffect, useRef } from "react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";
import { invalidateDocumentQueries, LIVE_QUERY_OPTIONS, pollWhileListPending } from "@/lib/live-documents";
import { ListPager, fetchPage, rememberPageSize, storedPageSize } from "@/components/list-pager";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Signatures() {
  const [, setLocation] = useLocation();
  const searchParams = useSearch();
  const urlParams = new URLSearchParams(searchParams);
  const statusFilter = urlParams.get("status") || "all";
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [user, setUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (!token) {
      setLocation("/login");
      return;
    }
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [setLocation]);

  // Paged and filtered in the API (it used to return only the 20 newest rows; tabs and search looked only at those).
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => storedPageSize("signaturesPageSize"));
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  useEffect(() => setPage(1), [query, statusFilter, pageSize]);

  const { data: listData, isLoading, isPlaceholderData } = useQuery<{ items: any[]; total: number }>({
    queryKey: ["/api/signatures", "list", { page, pageSize, query, statusFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), includeTotal: "true" });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (query) params.set("search", query);
      return fetchPage(getApiUrl(`/api/signatures?${params}`), getAuthHeaders());
    },
    placeholderData: keepPreviousData,
    ...LIVE_QUERY_OPTIONS,
    refetchInterval: pollWhileListPending,
  });
  const signatures = listData?.items;
  const total = listData?.total ?? 0;

  const { data: counts } = useQuery<Record<string, number>>({
    queryKey: ["/api/signatures", "counts"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/signatures/counts"), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    ...LIVE_QUERY_OPTIONS,
  });

  const [resendingId, setResendingId] = useState<string | null>(null);
  const [manualCompleteOpen, setManualCompleteOpen] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<any>(null);
  const [signedDocFile, setSignedDocFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resendMutation = useMutation({
    mutationFn: async (id: string) => {
      setResendingId(id);
      const res = await apiRequest("POST", `/api/signatures/${id}/resend`, {}, getAuthHeaders());
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/signatures"] });
      toast({ title: t("signatures.emailResent") });
      setResendingId(null);
    },
    onError: (error: any) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      setResendingId(null);
    },
  });

  const manualCompleteMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File | null }) => {
      const formData = new FormData();
      if (file) {
        formData.append("signedDocument", file);
      }
      
      const token = localStorage.getItem("token");
      // Use absolute API URL so the request targets the backend instead of the Vite dev server
      const url = getApiUrl(`/api/signatures/${id}/manual-complete`);
      const res = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateDocumentQueries(queryClient);
      toast({ 
        title: language === "pt" ? "Assinatura confirmada" : "Signature confirmed",
        description: language === "pt" 
          ? "A assinatura GOV.BR foi marcada como completa" 
          : "The GOV.BR signature has been marked as complete"
      });
      setManualCompleteOpen(false);
      setSelectedSignature(null);
      setSignedDocFile(null);
    },
    onError: (error: any) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const openManualCompleteDialog = (sig: any) => {
    setSelectedSignature(sig);
    setSignedDocFile(null);
    setManualCompleteOpen(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "signed": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "declined": return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const filteredSignatures = signatures || [];
  const nf = (n: number) => n.toLocaleString(language === "pt" ? "pt-BR" : "en-US");

  const hasActiveFilters = searchQuery.trim() !== "";
  
  const clearFilters = () => {
    setSearchQuery("");
  };

  if (!user) return null;

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/signatures">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">{t("signatures.title")}</h1>
            <p className="text-muted-foreground">{t("signatures.subtitle")}</p>
          </div>
          {counts && counts.all > 0 && (
            <div className="flex flex-wrap gap-2 sm:gap-3 text-sm" data-testid="signature-counts">
              <div className="px-3 py-1.5 rounded-full bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-300 font-medium">
                <Clock className="h-3.5 w-3.5 inline mr-1" />{nf(counts.pending ?? 0)} {language === "pt" ? "pendentes" : "pending"}
              </div>
              <div className="px-3 py-1.5 rounded-full bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300 font-medium">
                <CheckCircle className="h-3.5 w-3.5 inline mr-1" />{nf(counts.signed ?? 0)} {language === "pt" ? "assinados" : "signed"}
              </div>
              <div className="px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 font-medium">
                <XCircle className="h-3.5 w-3.5 inline mr-1" />{nf(counts.declined ?? 0)} {language === "pt" ? "recusados" : "declined"}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={language === "pt" ? "Buscar por documento ou signatário..." : "Search by document or signer..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search"
              />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
                <X className="h-4 w-4 mr-1" />
                {language === "pt" ? "Limpar" : "Clear"}
              </Button>
            )}
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground">
              {language === "pt" 
                ? `${nf(total)} de ${nf(counts?.all ?? total)} solicitações`
                : `${nf(total)} of ${nf(counts?.all ?? total)} requests`}
            </p>
          )}
        </div>

        <Tabs value={statusFilter} onValueChange={(value) => setLocation(`/signatures${value === "all" ? "" : `?status=${value}`}`)} className="mb-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">{t("signatures.all")}</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">{t("signatures.pending")}</TabsTrigger>
            <TabsTrigger value="signed" data-testid="tab-signed">{t("signatures.signed")}</TabsTrigger>
            <TabsTrigger value="declined" data-testid="tab-declined">{t("signatures.declined")}</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 w-1/3 bg-muted rounded mb-2" />
                  <div className="h-3 w-1/4 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSignatures.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Send className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">
                {statusFilter === "all" && searchQuery === "" 
                  ? t("signatures.noRequests") 
                  : t("signatures.noFilteredRequests")}
              </h3>
              <p className="text-muted-foreground">
                {statusFilter === "all" && searchQuery === ""
                  ? (language === "pt" 
                    ? "Comece criando um documento e adicionando signatários" 
                    : "Start by creating a document and adding signers")
                  : (language === "pt"
                    ? "Tente ajustar os filtros de busca"
                    : "Try adjusting your search filters")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-4 ${isPlaceholderData ? "opacity-60" : ""}`}>
            {filteredSignatures.map((sig: any) => (
              <Card key={sig.id} className="hover-elevate" data-testid={`card-signature-${sig.id}`}>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-4 flex-wrap">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                      {getStatusIcon(sig.status)}
                      <div>
                        <p className="font-medium">{sig.recipientName}</p>
                        <p className="text-sm text-muted-foreground">{sig.recipientEmail}</p>
                        {sig.document && (
                          <div className="flex items-center gap-1 mt-1">
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground">{sig.document.name}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground hidden sm:block">
                        {new Date(sig.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US", {
                          day: "2-digit",
                          month: "2-digit", 
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                      <Badge variant={sig.status === "signed" ? "default" : sig.status === "declined" ? "destructive" : "secondary"}>
                        {sig.status === "signed" ? t("signatures.signed") : 
                         sig.status === "declined" ? t("signatures.declined") : 
                         sig.status === "pending" ? t("signatures.pending") : sig.status}
                      </Badge>
                      {sig.document && (
                        <Link href={`/documents/${sig.document.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`button-view-document-${sig.id}`}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            {t("signatures.viewDocument")}
                          </Button>
                        </Link>
                      )}
                      {(sig.status === "pending" || sig.status === "viewed") && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resendingId === sig.id || resendMutation.isPending}
                          onClick={() => resendMutation.mutate(sig.id)}
                          data-testid={`button-resend-${sig.id}`}
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${resendingId === sig.id ? "animate-spin" : ""}`} />
                          {resendingId === sig.id ? t("signatures.resending") : t("signatures.resend")}
                        </Button>
                      )}
                      {(sig.status === "pending" || sig.status === "viewed") && sig.signatureMethod === "govbr" && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openManualCompleteDialog(sig)}
                          data-testid={`button-mark-complete-${sig.id}`}
                        >
                          <CheckSquare className="h-4 w-4 mr-1" />
                          {language === "pt" ? "Confirmar Assinatura" : "Mark Complete"}
                        </Button>
                      )}
                    </div>
                  </div>
                  {sig.signatureMethod === "govbr" && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">
                        GOV.BR
                      </Badge>
                      {sig.recipientPhone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {sig.recipientPhone}
                        </span>
                      )}
                      {sig.status === "pending" && (
                        <span className="text-xs">
                          {language === "pt" 
                            ? "Aguardando documento assinado via assinador.iti.br" 
                            : "Awaiting signed document via assinador.iti.br"}
                        </span>
                      )}
                      {sig.manuallyCompletedAt && (
                        <span className="text-xs text-green-600 dark:text-green-400">
                          {language === "pt" 
                            ? `Confirmado manualmente em ${new Date(sig.manuallyCompletedAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` 
                            : `Manually confirmed on ${new Date(sig.manuallyCompletedAt).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            <ListPager
              page={page}
              pageSize={pageSize}
              total={total}
              onPage={setPage}
              onPageSize={(size) => { setPageSize(size); rememberPageSize("signaturesPageSize", size); }}
              testId="signatures-pager"
            />
          </div>
        )}

        <Dialog open={manualCompleteOpen} onOpenChange={setManualCompleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {language === "pt" ? "Confirmar Assinatura GOV.BR" : "Confirm GOV.BR Signature"}
              </DialogTitle>
              <DialogDescription>
                {language === "pt" 
                  ? "Confirme que o signatário assinou o documento usando o assinador GOV.BR. Opcionalmente, faça upload do documento assinado." 
                  : "Confirm that the signer has signed the document using GOV.BR assinador. Optionally, upload the signed document."}
              </DialogDescription>
            </DialogHeader>
            
            {selectedSignature && (
              <div className="space-y-4 pt-2">
                <div className="bg-muted p-4 rounded-md space-y-2">
                  <p className="font-medium">{selectedSignature.recipientName}</p>
                  <p className="text-sm text-muted-foreground">{selectedSignature.recipientEmail}</p>
                  {selectedSignature.recipientPhone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {selectedSignature.recipientPhone}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>
                    {language === "pt" 
                      ? "Documento assinado (opcional)" 
                      : "Signed document (optional)"}
                  </Label>
                  <div 
                    className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover-elevate"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => setSignedDocFile(e.target.files?.[0] || null)}
                      data-testid="input-signed-document"
                    />
                    {signedDocFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm">{signedDocFile.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {language === "pt" 
                            ? "Clique para fazer upload do PDF assinado" 
                            : "Click to upload the signed PDF"}
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === "pt" 
                      ? "Faça upload do documento que o signatário retornou após assinar no assinador.iti.br" 
                      : "Upload the document the signer returned after signing on assinador.iti.br"}
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setManualCompleteOpen(false)}
                data-testid="button-cancel-manual-complete"
              >
                {language === "pt" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={() => selectedSignature && manualCompleteMutation.mutate({ 
                  id: selectedSignature.id, 
                  file: signedDocFile 
                })}
                disabled={manualCompleteMutation.isPending}
                data-testid="button-confirm-manual-complete"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {manualCompleteMutation.isPending 
                  ? (language === "pt" ? "Confirmando..." : "Confirming...")
                  : (language === "pt" ? "Confirmar Assinatura" : "Confirm Signature")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </AppLayout>
  );
}
